import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api/apiClient';
import {
  CreateBusinessCourseRequest,
  UpdateBusinessCourseRequest,
  BusinessCourseListParams,
  BusinessCourseResponse,
  BusinessCourseListResponse,
} from '@/constants/types';
import { API_ENDPOINTS } from '@/constants/endpoints';

const buildQuery = (params?: BusinessCourseListParams): string => {
  const searchParams = new URLSearchParams();

  if (params?.search) searchParams.append('search', params.search);
  if (params?.q) searchParams.append('q', params.q);
  if (params?.businessId) searchParams.append('businessId', params.businessId);
  if (params?.level) searchParams.append('level', params.level);
  if (params?.language) searchParams.append('language', params.language);
  if (params?.courseType) searchParams.append('courseType', params.courseType);
  if (params?.accessType) searchParams.append('accessType', params.accessType);
  if (params?.certificationAvailable !== undefined)
    searchParams.append('certificationAvailable', params.certificationAvailable.toString());
  if (params?.promoted !== undefined) searchParams.append('promoted', params.promoted.toString());
  if (params?.minPrice !== undefined) searchParams.append('minPrice', params.minPrice.toString());
  if (params?.maxPrice !== undefined) searchParams.append('maxPrice', params.maxPrice.toString());
  if (params?.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.limit) searchParams.append('limit', params.limit.toString());

  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

export const businessCourseService = {
  async getBusinessCourses(params?: BusinessCourseListParams): Promise<BusinessCourseListResponse> {
    return await apiGet<BusinessCourseListResponse>(
      `${API_ENDPOINTS.BUSINESS_COURSES.GET_ALL}${buildQuery(params)}`
    );
  },

  async getPublicBusinessCourses(params?: BusinessCourseListParams): Promise<BusinessCourseListResponse> {
    return await apiGet<BusinessCourseListResponse>(
      `${API_ENDPOINTS.BUSINESS_COURSES.GET_PUBLIC}${buildQuery(params)}`
    );
  },

  async getMyBusinessCourses(params?: BusinessCourseListParams): Promise<BusinessCourseListResponse> {
    return await apiGet<BusinessCourseListResponse>(
      `${API_ENDPOINTS.BUSINESS_COURSES.MY_ITEMS}${buildQuery(params)}`
    );
  },

  async getBusinessCourse(id: string): Promise<BusinessCourseResponse> {
    return await apiGet<BusinessCourseResponse>(API_ENDPOINTS.BUSINESS_COURSES.GET_BY_ID(id));
  },

  async getPublicBusinessCourse(id: string): Promise<BusinessCourseResponse> {
    return await apiGet<BusinessCourseResponse>(API_ENDPOINTS.BUSINESS_COURSES.GET_PUBLIC_BY_ID(id));
  },

  async createBusinessCourse(data: CreateBusinessCourseRequest): Promise<BusinessCourseResponse> {
    return await apiPost<BusinessCourseResponse>(API_ENDPOINTS.BUSINESS_COURSES.CREATE, data);
  },

  async updateBusinessCourse(id: string, data: UpdateBusinessCourseRequest): Promise<BusinessCourseResponse> {
    return await apiPut<BusinessCourseResponse>(API_ENDPOINTS.BUSINESS_COURSES.UPDATE(id), data);
  },

  async deleteBusinessCourse(id: string): Promise<{ success: boolean; message: string }> {
    return await apiDelete<{ success: boolean; message: string }>(API_ENDPOINTS.BUSINESS_COURSES.DELETE(id));
  },

  async reportBusinessCourse(id: string, reason: string): Promise<{ success: boolean; message: string; data?: any }> {
    return await apiPost<{ success: boolean; message: string; data?: any }>(
      API_ENDPOINTS.BUSINESS_COURSES.REPORT(id),
      { reason }
    );
  },

  async getBusinessCourseReports(page = 1, limit = 10): Promise<any> {
    return await apiGet<any>(`${API_ENDPOINTS.BUSINESS_COURSES.REPORTS}?page=${page}&limit=${limit}`);
  },
};
